import { useEffect, useState } from 'react'

const POLL_INTERVAL_MS = 200

/**
 * Reads a URL from localStorage, polling until it is written by the async
 * env-config fetch in index.html. Returns the fallback immediately on mount
 * so consumers never receive null/undefined, and updates once the real value
 * arrives (or stops after timeoutMs).
 */
export function useLocalStorageUrl(key: string, fallback: string, timeoutMs = 10_000): string {
  const [url, setUrl] = useState<string>(() => localStorage.getItem(key) || fallback)

  useEffect(() => {
    // Sync immediately whenever key or fallback changes
    const current = localStorage.getItem(key)
    setUrl(current || fallback)

    if (current) return // already populated — no need to poll

    const startTime = Date.now()
    const interval = setInterval(() => {
      const value = localStorage.getItem(key)
      if (value) {
        setUrl(value)
        clearInterval(interval)
      } else if (Date.now() - startTime >= timeoutMs) {
        clearInterval(interval)
      }
    }, POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [key, fallback, timeoutMs])

  return url
}
